/**
 * gamepadFocus.ts — Centralized controller-driven UI focus + navigation
 *
 * BETA 16.76 — Per Dylan: "you must build an entire usable interface
 * for controller so i can navigate around the ui in game and start
 * screen in all menues" + "i need there to be a box that outlines what
 * im going to click on when i use directional pad or anaolg stick. abxy
 * still not working. b shlound be back. a should be click. and x in
 * game should open all chest."
 *
 * MODEL:
 *   - Each named "screen" registers an ORDERED list of focusable
 *     targets, each with an `id`, an approximate rect, an `onActivate`
 *     callback, and an optional `onBack` callback.
 *   - One target per screen is the "currently focused" one. dpad / left
 *     stick cycles focus along the ordered list (with a basic
 *     spatial/2D awareness so up/down/left/right route to the right
 *     neighbor).
 *   - A button → focused target.onActivate()
 *   - B button → screen.onBack() OR the focused target.onBack()
 *   - The host renders a visual outline at the focused target's rect
 *     (via getFocusedTarget() each frame).
 *
 * USAGE:
 *   1. On screen mount, host calls registerFocusScreen(name, targets[], onBack)
 *   2. On screen unmount, host calls unregisterFocusScreen(name)
 *   3. Host activates a screen with setActiveScreen(name)
 *   4. Host polls tickGamepadFocus(pad) every frame (typically from
 *      gamepadInput.tickGamepad after the existing in-game button mapping)
 *   5. Host renders a focus outline by reading getFocusedTarget()
 *
 * The host is responsible for converting target rects to actual screen
 * coordinates (because rects may be in canvas coords, DOM coords, or
 * world coords depending on the screen). We keep this abstract.
 */

// BETA 19.68 — PSBOT cycle-30ba Bug #2: tag gamepad as active input source
// when nav fires so engine.ts focus-box render only shows during gamepad
// play (not stuck on first card in PC/mouse mode).
import { noteInputSource } from './inputSource';

export interface FocusTarget {
  id: string;
  /** Target rect in screen coords (px from viewport top-left). The
   *  host computes this AT registration time and updates if the layout
   *  changes (e.g. window resize). Used for both spatial nav and
   *  rendering the focus outline. */
  rect: { x: number; y: number; w: number; h: number };
  /** What pressing A does. Required. */
  onActivate: () => void;
  /** Optional secondary action (e.g. "select this item without buying"). */
  onSecondary?: () => void;
}

interface FocusScreen {
  name: string;
  targets: FocusTarget[];
  onBack?: () => void;
  /** Optional default focused index when the screen activates. */
  defaultIndex?: number;
}

const _screens = new Map<string, FocusScreen>();
let _activeScreen: string | null = null;
let _focusedId: string | null = null;
let _navCooldown = 0; // seconds until the next nav input is allowed
// BETA 19.72 — Dylan live: "with anlog stick with controllers you are are
// only able to move one hit box at a time with the. not hold analog stick
// and be able to shuffel across them for infinity." Was: single 0.28s gate
// for every step — felt like nothing happened when stick held. Now: classic
// keyboard auto-repeat pattern.
//
//   INITIAL_NAV_DELAY (0.28s) — Time from press → first repeat. Same
//     deliberate-tap behavior from 19.68 (no skipping from light wiggles).
//   REPEAT_NAV_INTERVAL (0.09s) — Fast follow-up cadence when the same
//     direction is held continuously past the initial delay (~11 cards/sec).
//   Same direction = same dir char ('left'/'right'/'up'/'down'). A new
//     direction or stick-release resets the hold timer back to initial delay.
//
// STICK_DEADZONE (0.55) preserved from 19.68 — light touches still ignored.
const INITIAL_NAV_DELAY = 0.28;
const REPEAT_NAV_INTERVAL = 0.09;
const STICK_DEADZONE = 0.55;

// BETA 19.72 — Track the direction currently being HELD. When the same
// direction is held continuously, transition from initial delay → fast
// repeat. Cleared (= null) when direction releases or changes.
let _lastNavDir: 'up' | 'down' | 'left' | 'right' | null = null;

export function registerFocusScreen(
  name: string,
  targets: FocusTarget[],
  onBack?: () => void,
  defaultIndex = 0,
): void {
  _screens.set(name, { name, targets, onBack, defaultIndex });
  if (_activeScreen === name) {
    // Refresh focused id if the active screen's targets changed
    if (!targets.find(t => t.id === _focusedId)) {
      _focusedId = targets[defaultIndex]?.id ?? null;
    }
  }
}

export function unregisterFocusScreen(name: string): void {
  _screens.delete(name);
  if (_activeScreen === name) {
    _activeScreen = null;
    _focusedId = null;
  }
}

export function setActiveScreen(name: string | null): void {
  _activeScreen = name;
  if (!name) {
    _focusedId = null;
    return;
  }
  const s = _screens.get(name);
  if (!s) {
    _focusedId = null;
    return;
  }
  _focusedId = s.targets[s.defaultIndex ?? 0]?.id ?? null;
}

export function getActiveScreen(): string | null { return _activeScreen; }
export function getFocusedTarget(): FocusTarget | null {
  if (!_activeScreen) return null;
  const s = _screens.get(_activeScreen);
  if (!s) return null;
  return s.targets.find(t => t.id === _focusedId) ?? null;
}

export function setFocusedId(id: string): void {
  _focusedId = id;
}

// BETA 18.9 — Synthetic focus advance from any input source (keyboard
// arrow keys, future touch swipes, etc.). Centralized so all input
// paths converge on the same spatial-nav logic.
// BETA 18.13 — Now also respects _navCooldown so keyboard arrows can't
// bypass the rate-limit that tickGamepadFocus uses. Prevents double-
// advance if gamepad D-pad AND keyboard arrows fire on the same press
// (some handhelds emulate keyboard from gamepad even in Gamepad mode).
export function advanceFocus(dir: 'up' | 'down' | 'left' | 'right'): boolean {
  if (!_activeScreen) return false;
  if (_navCooldown > 0) return false; // rate-limit shared with gamepad path
  const s = _screens.get(_activeScreen);
  if (!s || s.targets.length === 0) return false;
  const cur = s.targets.find(t => t.id === _focusedId) ?? s.targets[s.defaultIndex ?? 0];
  if (!cur) return false;
  const nxt = findNeighbor(cur, s.targets, dir);
  if (nxt) {
    _focusedId = nxt.id;
    // BETA 19.72 — Keyboard arrow synthetic calls always use INITIAL delay
    // (each arrow press is a discrete event, not a held analog stick).
    _navCooldown = INITIAL_NAV_DELAY;
    // BETA 19.68 — Tag gamepad as the active input source so engine.ts focus
    // box renders. Cleared when user moves mouse next.
    noteInputSource('gamepad');
    return true;
  }
  return false;
}

// BETA 18.9 — Activate the currently-focused target. Used by keyboard
// Enter / Space and gamepad A button.
export function activateFocused(): boolean {
  if (!_activeScreen) return false;
  const s = _screens.get(_activeScreen);
  if (!s) return false;
  const focused = s.targets.find(t => t.id === _focusedId);
  if (focused) {
    focused.onActivate();
    return true;
  }
  return false;
}

/** BETA 19.21 — Activate a specific target on the active screen by id,
 *  WITHOUT first moving focus there. Used by shortcut keys (e.g. gamepad
 *  Y on level-up / shrine / chest screens → fire the 'reroll' target
 *  directly). Returns true if a target with that id existed on the
 *  active screen and was fired. */
export function activateTargetById(id: string): boolean {
  if (!_activeScreen) return false;
  const s = _screens.get(_activeScreen);
  if (!s) return false;
  const target = s.targets.find(t => t.id === id);
  if (target) {
    target.onActivate();
    return true;
  }
  return false;
}

/** Pick the spatially-nearest target in the given direction. Falls
 *  back to wrapping along the registration order when no neighbor
 *  matches well — so a flat list (e.g. menu items) cycles cleanly. */
function findNeighbor(
  current: FocusTarget,
  targets: FocusTarget[],
  dir: 'up' | 'down' | 'left' | 'right',
): FocusTarget | null {
  const cx = current.rect.x + current.rect.w / 2;
  const cy = current.rect.y + current.rect.h / 2;
  let best: { t: FocusTarget; d: number } | null = null;
  for (const t of targets) {
    if (t.id === current.id) continue;
    const tx = t.rect.x + t.rect.w / 2;
    const ty = t.rect.y + t.rect.h / 2;
    const dx = tx - cx;
    const dy = ty - cy;
    // Direction filter — must be primarily in the requested direction.
    let primary = 0;
    let perpAbs = 0;
    if (dir === 'up')    { primary = -dy; perpAbs = Math.abs(dx); }
    if (dir === 'down')  { primary =  dy; perpAbs = Math.abs(dx); }
    if (dir === 'left')  { primary = -dx; perpAbs = Math.abs(dy); }
    if (dir === 'right') { primary =  dx; perpAbs = Math.abs(dy); }
    if (primary <= 0) continue; // wrong side
    // BETA 18.11 — Per Dylan: "i cant easliy go one at a time right or
    // left it skips around and unpredictable." Was: perpAbs * 1.5 — too
    // light a penalty, so from card_2 pressing 'right' could jump diagonally
    // to the reroll button (below + right) instead of cycling to card_0.
    // Now: perpAbs * 5 (strong same-row preference) + hard reject if
    // perpAbs > primary * 1.5 (so 'right' WILL NOT pick a target that's
    // more vertically away than it is horizontally close).
    if (perpAbs > primary * 1.5) continue;
    const score = primary + perpAbs * 5;
    if (!best || score < best.d) {
      best = { t, d: score };
    }
  }
  if (best) return best.t;
  // BETA 18.13 — Same-row-aware fallback. Old behavior was: when no
  // spatial neighbor passed the primary>0 filter, cycle in raw
  // registration order. That caused D-right from level-up card_2 to
  // jump to reroll (because reroll is index 3 of [card_0, card_1,
  // card_2, reroll] — last item, next-after-card_2). Then A on reroll
  // wastes a reroll instead of picking a card. Dylan: "level up same
  // unpredicatable bug." Now: for left/right, fallback cycles ONLY
  // among targets at roughly the same y. For up/down, cycles ONLY
  // among targets at roughly the same x. If no same-row/col peers
  // exist, full list-cycle as before (no regression for vertical
  // pause-menu etc.).
  const cardHEst = current.rect.h;
  const cardWEst = current.rect.w;
  const sameRowOrCol = (dir === 'left' || dir === 'right')
    ? targets.filter(t => Math.abs((t.rect.y + t.rect.h / 2) - cy) < cardHEst * 0.5)
    : targets.filter(t => Math.abs((t.rect.x + t.rect.w / 2) - cx) < cardWEst * 0.5);
  if (sameRowOrCol.length >= 2) {
    const i = sameRowOrCol.findIndex(t => t.id === current.id);
    if (i >= 0) {
      if (dir === 'up' || dir === 'left') {
        return sameRowOrCol[(i - 1 + sameRowOrCol.length) % sameRowOrCol.length];
      }
      return sameRowOrCol[(i + 1) % sameRowOrCol.length];
    }
  }
  const idx = targets.findIndex(t => t.id === current.id);
  if (idx < 0) return null;
  if (dir === 'up' || dir === 'left') {
    return targets[(idx - 1 + targets.length) % targets.length];
  }
  return targets[(idx + 1) % targets.length];
}

/**
 * BETA 22.74 — shared pad direction reader (extracted from tickGamepadFocus
 * so the DOM StartMenu poller reuses it instead of re-deriving it). This is
 * the ONE place that knows every d-pad encoding — including the ROG Ally
 * firmware quirk below. Priority: d-pad buttons 12-15 → axes[9] 8-way alt
 * encoding → axes[6]/[7] alt axes → left stick → right stick.
 *
 * BETA 18.2 — Dylan (ROG Ally X): "i cant move the hit box on any items
 * when leveling up, chest, or shrines." Most likely cause: some handhelds
 * (older ROG Ally firmware, certain Steam Input layouts, Linux gamepad
 * bindings) report the D-pad as a SINGLE AXIS at axes[9] instead of as
 * buttons 12-15. axes[9] uses 8 discrete values 1.0/0.71/0.43/0.14/-0.14/
 * -0.43/-0.71/-1.0 mapping to the 8 directional positions.
 * BETA 18.5 — right analog stick also navigates (Dylan: "does it work with
 * my analog sticks too?"). Same deadzone as left stick.
 */
export function readPadDirection(pad: Gamepad): 'up' | 'down' | 'left' | 'right' | null {
  const dpadUp = !!pad.buttons[12]?.pressed;
  const dpadDown = !!pad.buttons[13]?.pressed;
  const dpadLeft = !!pad.buttons[14]?.pressed;
  const dpadRight = !!pad.buttons[15]?.pressed;
  const sx = pad.axes[0] ?? 0;
  const sy = pad.axes[1] ?? 0;
  // Optional D-pad-as-axis fallback (axes[9] is the standard alt encoding;
  // axes[6]/[7] are the secondary-stick alt that some Linux drivers use).
  const dpadAx = pad.axes[9] ?? 999;
  const dpadAxIsValid = dpadAx >= -1.01 && dpadAx <= 1.01;
  let altDpad: 'up' | 'down' | 'left' | 'right' | null = null;
  if (dpadAxIsValid) {
    // axes[9] discrete dpad encoding:
    //  -1.00 = up, -0.71 = up+right, -0.43 = right, -0.14 = right+down,
    //   0.14 = down, 0.43 = down+left, 0.71 = left, 1.00 = up+left
    if (dpadAx <= -0.85) altDpad = 'up';
    else if (dpadAx >= -0.55 && dpadAx <= -0.30) altDpad = 'right';
    else if (dpadAx >= 0.00 && dpadAx <= 0.30) altDpad = 'down';
    else if (dpadAx >= 0.55 && dpadAx <= 0.85) altDpad = 'left';
  }
  // Also catch axes[6]/[7] (alternate horizontal/vertical dpad-as-axis).
  const dpadAxH = pad.axes[6] ?? 0;
  const dpadAxV = pad.axes[7] ?? 0;
  const rx = pad.axes[2] ?? 0;
  const ry = pad.axes[3] ?? 0;
  const stickActive = Math.abs(sx) > STICK_DEADZONE || Math.abs(sy) > STICK_DEADZONE;
  const rStickActive = Math.abs(rx) > STICK_DEADZONE || Math.abs(ry) > STICK_DEADZONE;
  if (dpadUp) return 'up';
  if (dpadDown) return 'down';
  if (dpadLeft) return 'left';
  if (dpadRight) return 'right';
  if (altDpad) return altDpad;
  if (Math.abs(dpadAxH) > 0.5) return dpadAxH < 0 ? 'left' : 'right';
  if (Math.abs(dpadAxV) > 0.5) return dpadAxV < 0 ? 'up' : 'down';
  if (stickActive) {
    if (Math.abs(sx) > Math.abs(sy)) return sx < 0 ? 'left' : 'right';
    return sy < 0 ? 'up' : 'down';
  }
  if (rStickActive) {
    if (Math.abs(rx) > Math.abs(ry)) return rx < 0 ? 'left' : 'right';
    return ry < 0 ? 'up' : 'down';
  }
  return null;
}

/**
 * Per-frame gamepad nav tick. Call from gamepadInput.tickGamepad AFTER
 * the in-game button mapping so menus only steal input when an active
 * screen is registered. Returns true if input was consumed (so the
 * host can skip its own gamepad handling for this frame).
 */
export function tickGamepadFocus(
  pad: Gamepad,
  dt: number,
  edge: { wasPressed: (i: number) => boolean; isPressed: (i: number) => boolean },
): boolean {
  if (!_activeScreen) return false;
  const s = _screens.get(_activeScreen);
  if (!s || s.targets.length === 0) return false;

  _navCooldown = Math.max(0, _navCooldown - dt);

  // Direction input — all encodings live in readPadDirection (BETA 22.74:
  // extracted so the DOM StartMenu poller shares the exact same decoding,
  // including the ROG Ally d-pad-as-axis quirk).
  const dir = readPadDirection(pad);

  if (dir && _navCooldown <= 0) {
    const current = s.targets.find(t => t.id === _focusedId) ?? s.targets[s.defaultIndex ?? 0];
    const next = findNeighbor(current, s.targets, dir);
    if (next) {
      _focusedId = next.id;
      // BETA 19.72 — Auto-repeat: first step waits INITIAL_NAV_DELAY,
      // subsequent steps (while same direction held) at REPEAT_NAV_INTERVAL.
      // If direction CHANGED since last tick, treat as a fresh press → initial
      // delay. If same direction continues, use the fast repeat interval.
      // BETA 20.25 — Dylan: on the PICK screens (level-up / shrine / chest),
      // holding the stick or d-pad must move ONLY ONE hit box, then wait for the
      // stick to return to neutral — no auto-scrub through cards (that caused
      // accidentally picking the wrong upgrade). Block any repeat until neutral;
      // the `!dir` branch below resets _navCooldown to 0, re-arming the next
      // deliberate press. Menu grids (chalkboard_*) keep the hold-to-scroll repeat.
      const _isPickScreen = _activeScreen === 'levelup_cards' || _activeScreen === 'shrine_cards' || _activeScreen === 'chest_cards';
      _navCooldown = _isPickScreen ? 999 : ((_lastNavDir === dir) ? REPEAT_NAV_INTERVAL : INITIAL_NAV_DELAY);
      _lastNavDir = dir;
      // BETA 18.9 — Diagnostic console log so Dylan can open DevTools on
      // his Ally X and see live what the focus pipeline is doing.
      try { console.log('[gamepadFocus]', _activeScreen, dir, '→', next.id); } catch {}
    }
  } else if (!dir) {
    // No directional input — reset cooldown so the very next press is responsive
    // BETA 19.72 — also clear the held-direction tracker so next press starts
    // with the slow initial delay (deliberate tap), not the fast repeat rate.
    _navCooldown = 0;
    _lastNavDir = null;
  }

  // A button → activate
  const aJust = edge.isPressed(0) && !edge.wasPressed(0);
  if (aJust) {
    const focused = s.targets.find(t => t.id === _focusedId);
    if (focused) {
      focused.onActivate();
      return true;
    }
  }

  // B button → back
  const bJust = edge.isPressed(1) && !edge.wasPressed(1);
  if (bJust) {
    const focused = s.targets.find(t => t.id === _focusedId);
    if (focused?.onSecondary) {
      focused.onSecondary();
      return true;
    }
    if (s.onBack) {
      s.onBack();
      return true;
    }
  }

  // Always consume input when a screen is active so it doesn't bleed
  // into in-game logic.
  return true;
}
